import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

import {
  getActivePlanAssignmentsQueryKey,
  getPlanAndAssignmentsQueryKey,
  resetPlanSteps,
} from '@cfa/pathway-fe-shared/xp';
import { Button, Modal } from '@cfa/react-core';

import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { queryClient } from '@/services';

import { resetProgressModalOpenAtomFamily } from '../state';

interface ResetProgressModalProps {
  teamMemberId: string;
  teamMemberName: string;
  planId?: string;
  planName?: string;
}

const ResetProgressModal: React.FC<ResetProgressModalProps> = ({
  teamMemberId,
  teamMemberName,
  planName,
  planId,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useAtom(
    resetProgressModalOpenAtomFamily(teamMemberId),
  );

  const { mutate: resetProgress, isPending } = useMutation({
    mutationFn: async (planId: string) => {
      await resetPlanSteps({
        path: {
          planId,
        },
        body: {
          userIds: [teamMemberId],
        },
        throwOnError: true,
      });

      queryClient.invalidateQueries({
        queryKey: getActivePlanAssignmentsQueryKey(),
        refetchType: 'all',
      });

      await queryClient.refetchQueries({
        queryKey: getPlanAndAssignmentsQueryKey({
          path: { planId },
        }),
        type: 'all',
      });
    },
    onSuccess: () =>
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="success">
          {t('Generic.progressReset')}
        </ToastMessageBlock>
      )),
    onError: err => {
      datadogRum.addError(err);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {err.message}
        </ToastMessageBlock>
      ));
    },
    onSettled: () => {
      setIsOpen(false);
    },
  });

  const handleResetProgress = () => {
    if (!planId) {
      datadogRum.addError(
        new Error(
          '[ResetProgressModal] no plan id provided when resetting progress',
        ),
      );
      return;
    }
    resetProgress(planId);
  };

  if (!planName) {
    return null;
  }

  return (
    <Modal.Root isOpen={isOpen} onOpenChange={setIsOpen}>
      <Modal.Dialog className="w-[600px]!">
        <Modal.Content className="p-7! pb-11!">
          <Modal.Title className="text-variant-h3!">
            {t('Button.resetProgress')}
          </Modal.Title>
          <p className="text-variant-body1 pt-1 pb-3">
            <Trans
              i18nKey="Button.resetProgressModalBody"
              values={{ teamMemberName, planName }}
            />
          </p>
          <Modal.Footer className="gap-4!">
            <Button
              variant="outlined"
              color="secondary"
              className="w-full!"
              onPress={() => setIsOpen(false)}
            >
              {t('Button.cancel')}
            </Button>
            <Button
              data-testid="ResetProgressButton"
              className="bg-feedback-error-base! disabled:bg-disabled-base! disabled:text-disabled-contrast! disabled:border-disabled-base! border-feedback-error-base! hover:bg-feedback-error-hover! hover:border-feedback-error-hover! w-full! transition-colors"
              onPress={handleResetProgress}
              isDisabled={isPending}
            >
              {t('Button.reset')}
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Dialog>
    </Modal.Root>
  );
};

export default ResetProgressModal;
